import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";

import { fToNow } from "src/utils/format-time";

import Label from "src/components/label";
import FileThumbnail from "src/components/file-thumbnail";
import { APP_URL, HOST_API } from "src/config-global";
import { t } from "i18next";
import { RouterLink } from "src/routes/components";
import { paths } from "src/routes/paths";
import { useTheme } from "@mui/material/styles";
import { IconButton } from "@mui/material";
import Iconify from "src/components/iconify";
import { playerNotificationsMarkOneRead } from "src/api/user";
import { useLocales } from "src/locales/use-locales";
// ----------------------------------------------------------------------

type NotificationItemProps = {
  notification: {
    id: number;
    content: string;
    category: string;
    created_at: Date;
    status: number;
    type: NotificationType;
    avatarUrl: string | null;
    action_url1: string;
    action_url2: string;
    sender_name: string;
  };
  refetchNotifications: () => Promise<any>; // or more specific type if you have it
};

const MONTHLY_ACTIVITY_PREFIX = "notification_activity_last_month_";
const BOOKING_SPENT_PREFIX = "notification_booking_spent_last_month_";
const TOTAL_SPENT_PREFIX = "notification_total_spent_last_month_";

const NOTIFICATION_TYPES = [
  "systemmessage",
  MONTHLY_ACTIVITY_PREFIX,
  BOOKING_SPENT_PREFIX,
  TOTAL_SPENT_PREFIX,
] as const;

type NotificationType = (typeof NOTIFICATION_TYPES)[number] | string;

type ParsedNotificationContent =
  | { kind: "i18n"; key: string; params?: Record<string, string | number> }
  | { kind: "raw"; text: string };

export default function NotificationItem({
  notification,
  refetchNotifications,
}: NotificationItemProps) {
  const theme = useTheme();
  const { currentLang } = useLocales();
  const handleMarkOneAsRead = async (notification_id: number) => {
    try {
      await playerNotificationsMarkOneRead({
        notification_id: notification_id,
      });

      // refresh list from backend so statuses become 0 in UI
      await refetchNotifications();
    } catch (error) {
      console.error("Failed to mark notification as read", error);
      // optionally show snackbar/toast here
    }
  };

  const parsed = parseNotificationContent(
    notification.type,
    notification.content
  );

  const renderPrimaryText = () => {
    if (parsed.kind === "raw") {
      return parsed.text;
    }

    return t(parsed.key, parsed.params);
  };

  const renderAvatar = (
    <ListItemAvatar>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: "background.neutral",
        }}
      >
        <Box
          component="img"
          src={`/assets/icons/notification/${
            (notification.type === "systemmessage" && "ic_mail") ||
            (notification.type === "order" && "ic_order") ||
            (notification.type === "message" && "ic_chat") ||
            (notification.type === "mail" && "ic_mail") ||
            (notification.type === "delivery" && "ic_delivery") ||
            "ic_mail"
          }.svg`}
          sx={{ width: 24, height: 24 }}
        />
      </Stack>
      {/* {notification.avatarUrl ? (
        <Avatar
          src={HOST_API + "/" + notification.avatarUrl}
          sx={{ bgcolor: "background.neutral" }}
        />
      ) : (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: "background.neutral",
          }}
        >
          <Box
            component="img"
            src={`/assets/icons/notification/${
              (notification.type === "systemmessage" && "ic_mail") ||
              (notification.type === "order" && "ic_order") ||
              (notification.type === "message" && "ic_chat") ||
              (notification.type === "mail" && "ic_mail") ||
              (notification.type === "delivery" && "ic_delivery")
            }.svg`}
            sx={{ width: 24, height: 24 }}
          />
        </Stack>
      )} */}
    </ListItemAvatar>
  );

  const messageTitle = (
    <ListItemText
      primary={t("message") + ": " + notification.sender_name}
      secondary=""
    />
  );

  const notificationTitle = (
    <ListItemText
      primary={t("notification") + ": " + notification.sender_name}
      secondary=""
    />
  );

  const { typographyProps } = getPrimaryTextProps(notification.type);

  const renderText = (
    <ListItemText
      disableTypography
      primary={
        <Typography {...typographyProps} color={theme.palette.common.black}>
          {renderPrimaryText()}
        </Typography>
      }
      secondary={
        <Stack
          direction="row"
          alignItems="center"
          sx={{ typography: "caption", color: "text.disabled" }}
          divider={
            <Box
              sx={{
                width: 2,
                height: 2,
                bgcolor: "currentColor",
                mx: 0.5,
                borderRadius: "50%",
              }}
            />
          }
        >
          {fToNow(notification.created_at, currentLang.adapterLocale)}
        </Stack>
      }
    />
  );

  const renderUnReadBadge = notification.status === 1 && (
    <Box
      sx={{
        // top: 26,
        // right: 20,
        width: 8,
        height: 8,

        borderRadius: "50%",
        bgcolor: "success.main",
        // position: "absolute",
      }}
    />
  );

  const friendAction = (
    <Stack spacing={1} direction="row" sx={{ mt: 1.5 }}>
      <Button size="small" variant="contained">
        Accept
      </Button>
      <Button size="small" variant="outlined">
        Decline
      </Button>
    </Stack>
  );

  const projectAction = (
    <Stack alignItems="flex-start">
      <Box
        sx={{
          p: 1.5,
          my: 1.5,
          borderRadius: 1.5,
          color: "text.secondary",
          bgcolor: "background.neutral",
        }}
      >
        {reader(
          `<p><strong>@Jaydon Frankie</strong> feedback by asking questions or just leave a note of appreciation.</p>`
        )}
      </Box>

      <Button size="small" variant="contained">
        Reply
      </Button>
    </Stack>
  );

  const fileAction = (
    <Stack
      spacing={1}
      direction="row"
      sx={{
        pl: 1,
        p: 1.5,
        mt: 1.5,
        borderRadius: 1.5,
        bgcolor: "background.neutral",
      }}
    >
      <FileThumbnail
        file="http://localhost:8080/httpsdesign-suriname-2015.mp3"
        sx={{ width: 40, height: 40 }}
      />

      <Stack
        spacing={1}
        direction={{ xs: "column", sm: "row" }}
        flexGrow={1}
        sx={{ minWidth: 0 }}
      >
        <ListItemText
          disableTypography
          primary={
            <Typography
              variant="subtitle2"
              component="div"
              sx={{ color: "text.secondary" }}
              noWrap
            >
              design-suriname-2015.mp3
            </Typography>
          }
          secondary={
            <Stack
              direction="row"
              alignItems="center"
              sx={{ typography: "caption", color: "text.disabled" }}
              divider={
                <Box
                  sx={{
                    mx: 0.5,
                    width: 2,
                    height: 2,
                    borderRadius: "50%",
                    bgcolor: "currentColor",
                  }}
                />
              }
            >
              <span>2.3 GB</span>
              <span>30 min ago</span>
            </Stack>
          }
        />

        <Button size="small" variant="outlined">
          Download
        </Button>
      </Stack>
    </Stack>
  );

  const tagsAction = (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1.5 }}>
      <Label variant="outlined" color="info">
        Design
      </Label>
      <Label variant="outlined" color="warning">
        Dashboard
      </Label>
      <Label variant="outlined">Design system</Label>
    </Stack>
  );

  const messageAction = (
    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
      <Button
        component={RouterLink}
        href={paths.player.messages + "?id=" + notification.action_url1}
        color="primary"
        size="small"
        variant="contained"
      >
        {t("view")}
      </Button>
    </Stack>
  );

  const systemMessageAction = notification?.action_url1 && (
    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
      <Button
        component={RouterLink}
        href={"/" + notification.action_url1}
        color="primary"
        size="small"
        variant="contained"
      >
        {t("view")}
      </Button>
    </Stack>
  );

  const recoveryMessageAction = notification?.action_url1 && (
    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
      <Button
        component={RouterLink}
        href={APP_URL + notification.action_url1}
        color="primary"
        size="small"
        variant="contained"
      >
        {t("view")}
      </Button>
    </Stack>
  );

  const postNotificationAction = notification?.action_url2 && (
    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
      <Button
        component={RouterLink}
        href={APP_URL + notification.action_url2}
        color="primary"
        size="small"
        variant="contained"
      >
        {t("view")}
      </Button>
    </Stack>
  );

  const paymentAction = (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button size="small" variant="contained">
        Pay
      </Button>
      <Button size="small" variant="outlined">
        Decline
      </Button>
    </Stack>
  );

  return (
    <ListItemButton
      disableRipple
      sx={{
        p: 2.5,
        alignItems: "flex-start",
        borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
      }}
    >
      {renderAvatar}

      <Stack sx={{ flexGrow: 1 }}>
        {notification.type === "message" && messageTitle}
        {(notification.type.includes("recovery") ||
          notification.type.includes("provider_post_notif")) &&
          notificationTitle}
        {renderText}
        {notification.type.includes("recovery") && recoveryMessageAction}

        {notification.type.includes("provider_post_notif") &&
          postNotificationAction}

        {notification.type === "systemmessage" && systemMessageAction}
        {/* {notification.type === "player_monthly_activity_hours" && playerActivityMessageAction} */}

        {notification.type === "message" && messageAction}
        {notification.type === "friend" && friendAction}
        {notification.type === "project" && projectAction}
        {notification.type === "file" && fileAction}
        {notification.type === "tags" && tagsAction}
        {notification.type === "payment" && paymentAction}
      </Stack>
      <Stack direction={"row"} alignItems={"center"}>
        {renderUnReadBadge}
        {/* Mark one as read */}
        {!!notification.status && (
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleMarkOneAsRead(notification.id)}
            // sx={{ mt: 0.5 }} // small visual alignment tweak
          >
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        )}
      </Stack>
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function reader(data: string) {
  return (
    <Box
      dangerouslySetInnerHTML={{ __html: data }}
      sx={{
        mb: 0.5,
        "& p": { typography: "body2", m: 0 },
        "& a": { color: "inherit", textDecoration: "none" },
        "& strong": { typography: "subtitle2" },
      }}
    />
  );
}

function isMultiLineNotification(type: string): boolean {
  // Centralize your rules here
  return (
    type === "systemmessage" ||
    type.includes("recovery") ||
    type === "notification_activity_last_month" ||
    type === "notification_booking_spent_last_month" ||
    type === "notification_total_spent_last_month"
  );
}

function getPrimaryTextProps(type: string) {
  const multiline = isMultiLineNotification(type);

  return {
    multiline,
    typographyProps: multiline
      ? { variant: "body2" as const }
      : {
          noWrap: true,
          variant: "body2" as const,
          sx: {
            maxWidth: { xs: "250px", sm: "300px" },
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        },
  };
}

export function parseSuffixAmountCredit(
  content: string,
  prefix: string
): { amount: number; credit: number } | null {
  if (!content.startsWith(prefix)) return null;

  const suffix = content.slice(prefix.length);

  // Expected suffix format: "23352_(17)" or "23352_(0)"
  // Allow optional spaces around parentheses if you ever emit them.
  const m = suffix.match(/^(\d+(?:\.\d+)?)_\(\s*(\d+(?:\.\d+)?)\s*\)$/);
  if (!m) return null;

  const amount = Number(m[1]);
  const credit = Number(m[2]);

  if (!Number.isFinite(amount) || !Number.isFinite(credit)) return null;

  return { amount, credit };
}

function parseSuffixNumber(content: string, prefix: string): number | null {
  if (!content.startsWith(prefix)) return null;

  const suffix = content.slice(prefix.length);
  const n = Number(suffix);

  return Number.isFinite(n) ? n : null;
}

function parseNotificationContent(
  type: string,
  content: string
): ParsedNotificationContent {
  // 1) Monthly activity hours
  if (type === "notification_activity_last_month") {
    const hours = parseSuffixNumber(content, MONTHLY_ACTIVITY_PREFIX);
    if (hours !== null) {
      return {
        kind: "i18n",
        key: "notification_activity_last_month",
        params: { hours },
      };
    }
  }

  // 2) Booking-related spend last month
  if (type === "notification_booking_spent_last_month") {
    const parsed = parseSuffixAmountCredit(content, BOOKING_SPENT_PREFIX);
    if (parsed) {
      return {
        kind: "i18n",
        key: "notification_booking_spent_last_month",
        params: {
          amount: parsed.amount,
          credit: parsed.credit,
        },
      };
    }
  }

  // 3) Total spent last month
  if (type === "notification_total_spent_last_month") {
    const amount = parseSuffixNumber(content, TOTAL_SPENT_PREFIX);
    if (amount !== null) {
      return {
        kind: "i18n",
        key: "notification_total_spent_last_month",
        params: { amount },
      };
    }
  }

  // Default: treat content as translation key
  return { kind: "i18n", key: content };
}
